import React, { useState, useRef, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Sensors as SensorsIcon,
    SignalWifiStatusbar4Bar as OnlineIcon,
    SignalWifiOff as OfflineIcon,
    Map as MapIcon,
    AutoFixHigh as WizardIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import { useNavigate } from 'react-router-dom';
import type { IotObjekt, CreateIotObjektRequest, UpdateIotObjektRequest } from '../../api/JsdhNotifikaceService/generated';
import type { LokaceSearchResult } from '../../api/JsdhNotifikaceService/generated/models/LokaceSearchResult';
import type { DeviceDetail } from '../../api/JsdhNotifikaceService/generated/models/Device';
import {
    useIotObjektyQuery,
    useCreateIotObjektMutation,
    useUpdateIotObjektMutation,
    useDeleteIotObjektMutation,
} from '../../api/JsdhNotifikaceService/service/IotMutations';
import { useDevicesQuery } from '../../api/JsdhNotifikaceService/service/DeviceMutations';
import JsdhNotifikaceService from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import MapPickerDialog from '../../components/MapPickerDialog';
import OnboardingWizard from '../../components/iot/OnboardingWizard';
import { rpiNeozvalo } from '../../components/iot/iotStav';
import { POVOLIT_PRIDANI_ZIGBEE } from '../../api/JsdhNotifikaceService/service/IotZarizeniService';

const isValidGps = (gps: string | null | undefined): boolean => {
    if (!gps || !gps.trim()) return false;
    const parts = gps.split(',');
    if (parts.length !== 2) return false;
    const lat = Number(parts[0].trim());
    const lng = Number(parts[1].trim());
    if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const IoTObjektyPage: React.FC = () => {
    const navigate = useNavigate();
    const { data: objekty, isLoading, error } = useIotObjektyQuery();
    const { data: devices } = useDevicesQuery();
    const createMutation = useCreateIotObjektMutation();
    const updateMutation = useUpdateIotObjektMutation();
    const deleteMutation = useDeleteIotObjektMutation();

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [selectedObjekt, setSelectedObjekt] = useState<IotObjekt | null>(null);
    const [formData, setFormData] = useState<CreateIotObjektRequest>({
        nazev: '',
        adresa: '',
        obec: '',
        castObce: '',
        ulice: '',
        cisloPopisne: '',
        gps: '',
        deviceId: null,
    });

    // Autocomplete pro adresy
    const [adresaInput, setAdresaInput] = useState('');
    const [lokaceOptions, setLokaceOptions] = useState<LokaceSearchResult[]>([]);
    const [isLoadingLokace, setIsLoadingLokace] = useState(false);
    const [geocodedGps, setGeocodedGps] = useState<[number, number] | null>(null);
    const lokaceSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Mnozina device ID prirazeny k jinym objektum (pro disablovani v selectu)
    const assignedDeviceIds = useMemo(() => {
        if (!objekty) return new Set<number>();
        const ids = new Set<number>();
        for (const obj of objekty) {
            if (obj.deviceId != null && obj.id !== selectedObjekt?.id) {
                ids.add(obj.deviceId);
            }
        }
        return ids;
    }, [objekty, selectedObjekt]);

    const handleAdresaInputChange = (value: string) => {
        setAdresaInput(value);

        if (lokaceSearchTimeoutRef.current) {
            clearTimeout(lokaceSearchTimeoutRef.current);
        }

        if (value.length >= 2) {
            lokaceSearchTimeoutRef.current = setTimeout(async () => {
                setIsLoadingLokace(true);
                try {
                    const results = await JsdhNotifikaceService.searchLokace(value);
                    setLokaceOptions(results);
                } catch {
                    setLokaceOptions([]);
                }
                setIsLoadingLokace(false);
            }, 300);
        } else {
            setLokaceOptions([]);
        }
    };

    const handleLokaceSelect = (value: string | LokaceSearchResult | null) => {
        if (!value) return;

        if (typeof value === 'string') {
            setAdresaInput(value);
        } else {
            setAdresaInput(value.displayName);
            setFormData({
                ...formData,
                obec: value.obec || value.nazev,
                castObce: value.castObce || formData.castObce,
                ulice: value.ulice || formData.ulice,
                cisloPopisne: value.cisloPopisne || formData.cisloPopisne,
                gps: `${value.lat},${value.lng}`,
            });
            setGeocodedGps([value.lat, value.lng]);
        }
    };

    const handleGpsSelect = (lat: number, lng: number) => {
        setFormData({ ...formData, gps: `${lat},${lng}` });
    };

    const handleOpenCreate = () => {
        setSelectedObjekt(null);
        setFormData({
            nazev: '',
            adresa: '',
            obec: '',
            castObce: '',
            ulice: '',
            cisloPopisne: '',
            gps: '',
            deviceId: null,
        });
        setAdresaInput('');
        setLokaceOptions([]);
        setGeocodedGps(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (objekt: IotObjekt) => {
        setSelectedObjekt(objekt);
        setFormData({
            nazev: objekt.nazev,
            adresa: objekt.adresa || '',
            obec: objekt.obec || '',
            castObce: objekt.castObce || '',
            ulice: objekt.ulice || '',
            cisloPopisne: objekt.cisloPopisne || '',
            gps: objekt.gps || '',
            deviceId: objekt.deviceId ?? null,
        });
        setAdresaInput('');
        setLokaceOptions([]);
        // Nastavit geocodedGps z existujícího GPS pokud existuje
        if (objekt.gps) {
            const parts = objekt.gps.split(',');
            if (parts.length === 2) {
                const lat = parseFloat(parts[0].trim());
                const lng = parseFloat(parts[1].trim());
                if (!isNaN(lat) && !isNaN(lng)) {
                    setGeocodedGps([lat, lng]);
                }
            }
        } else {
            setGeocodedGps(null);
        }
        setOpenDialog(true);
    };

    const handleOpenDelete = (objekt: IotObjekt) => {
        setSelectedObjekt(objekt);
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedObjekt(null);
        setFormData({
            nazev: '',
            adresa: '',
            obec: '',
            castObce: '',
            ulice: '',
            cisloPopisne: '',
            gps: '',
            deviceId: null,
        });
        setAdresaInput('');
        setLokaceOptions([]);
        setGeocodedGps(null);
    };

    const handleSubmit = () => {
        if (selectedObjekt) {
            const updateData: UpdateIotObjektRequest = {
                nazev: formData.nazev,
                adresa: formData.adresa || null,
                obec: formData.obec || null,
                castObce: formData.castObce || null,
                ulice: formData.ulice || null,
                cisloPopisne: formData.cisloPopisne || null,
                gps: formData.gps || null,
                deviceId: formData.deviceId ?? null,
            };
            updateMutation.mutate(
                { id: selectedObjekt.id, data: updateData },
                { onSuccess: () => handleCloseDialog() }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => handleCloseDialog(),
            });
        }
    };

    const handleDelete = () => {
        if (selectedObjekt) {
            deleteMutation.mutate(selectedObjekt.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelectedObjekt(null);
                },
            });
        }
    };

    // Najdi vybrany device objekt pro Autocomplete
    const selectedDevice = useMemo(() => {
        if (!formData.deviceId || !devices) return undefined;
        return devices.find(d => d.id === formData.deviceId) ?? undefined;
    }, [formData.deviceId, devices]);

    // RPi sloupce zobrazit jen pokud aspon jeden objekt reálně má přiřazené RPi zařízení
    // (u čistě Shelly/WiFi nasazení jsou matoucí a zbytečné).
    const hasAnyRpi = useMemo(
        () => (objekty ?? []).some((o) => o.deviceId != null),
        [objekty],
    );

    const rpiColumns: GridColDef[] = [
        {
            field: 'deviceName',
            headerName: 'RPi zařízení',
            width: 150,
            renderCell: (params) => {
                const row = params.row as IotObjekt;
                if (row.deviceId == null) {
                    return <Typography variant="body2" color="text.secondary">-</Typography>;
                }
                if (row.deviceName) {
                    return row.deviceName;
                }
                if (row.deviceHostname) {
                    return row.deviceHostname;
                }
                return <Typography variant="body2" color="text.secondary">-</Typography>;
            },
        },
        {
            field: 'rpiOnline',
            headerName: 'RPi',
            width: 150,
            renderCell: (params) => {
                const row = params.row as IotObjekt;
                // Ne-RPi objekt (Shelly/WiFi) nemá smysl zobrazovat jako „RPi Offline"
                if (row.deviceId == null) {
                    return <Typography variant="body2" color="text.secondary">-</Typography>;
                }
                // Bez posledního kontaktu RPi „se zatím neozvalo“, není to výpadek (neutrálně, ne červeně).
                if (rpiNeozvalo(row)) {
                    return <Chip label="Zatím se neozvalo" size="small" variant="outlined" />;
                }
                return (
                    <Chip
                        icon={params.value ? <OnlineIcon /> : <OfflineIcon />}
                        label={params.value ? 'Online' : 'Offline'}
                        color={params.value ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
    ];

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'nazev', headerName: 'Název', flex: 1, minWidth: 200 },
        {
            field: 'obec',
            headerName: 'Obec',
            width: 150,
            renderCell: (params) => {
                const row = params.row as IotObjekt;
                if (row.castObce) {
                    return `${row.obec}, ${row.castObce}`;
                }
                return row.obec || '-';
            },
        },
        {
            field: 'ulice',
            headerName: 'Ulice',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const row = params.row as IotObjekt;
                if (row.ulice && row.cisloPopisne) {
                    return `${row.ulice} ${row.cisloPopisne}`;
                }
                return row.ulice || row.cisloPopisne || '-';
            },
        },
        ...(hasAnyRpi ? rpiColumns : []),
        {
            field: 'aktivni',
            headerName: 'Stav',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Aktivní' : 'Neaktivní'}
                    color={params.value ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'createdAt',
            headerName: 'Vytvořeno',
            width: 180,
            valueFormatter: (value: string) => {
                if (!value) return '';
                return new Date(value).toLocaleString('cs-CZ');
            },
        },
        {
            field: 'actions',
            headerName: 'Akce',
            width: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box>
                    <Tooltip title="Detail">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/admin/iot/objekty/${params.row.id}`)}
                        >
                            <ViewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Upravit">
                        <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Smazat">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDelete(params.row)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    if (error) {
        return <Alert severity="error">Chyba při načítání objektů: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="IoT Monitorované objekty"
                icon={<SensorsIcon />}
                action={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                    >
                        Přidat objekt
                    </Button>
                }
            />

            {!isLoading && objekty && objekty.length === 0 ? (
                <Paper sx={{ width: '100%', p: 5, textAlign: 'center' }}>
                    <SensorsIcon color="disabled" sx={{ fontSize: 56, mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                        Zatím žádné monitorované objekty
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
                        Přidejte objekt (např. hasičskou zbrojnici) a připojte k němu zařízení
                        {POVOLIT_PRIDANI_ZIGBEE ? 'Shelly / WiFi nebo Raspberry Pi / Zigbee.' : 'Shelly / WiFi.'} Průvodce vás krok za krokem
                        provede získáním přístupových údajů a nastavením zařízení.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                        >
                            Přidat objekt
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<WizardIcon />}
                            onClick={() => setWizardOpen(true)}
                        >
                            Průvodce zprovozněním
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <Paper sx={{ width: '100%' }}>
                    <DataGrid
                        rows={objekty || []}
                        columns={columns}
                        loading={isLoading}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                        disableRowSelectionOnClick
                        localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                    />
                </Paper>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedObjekt ? 'Upravit objekt' : 'Nový monitorovaný objekt'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Název objektu"
                            value={formData.nazev}
                            onChange={(e) => setFormData({ ...formData, nazev: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                            helperText="Např. Hasičská zbrojnice, Obecní úřad"
                        />

                        <Autocomplete<DeviceDetail, false, false, false>
                            options={devices || []}
                            value={selectedDevice}
                            onChange={(_, value) => setFormData({ ...formData, deviceId: value?.id ?? null })}
                            getOptionLabel={(option) =>
                                option.name
                                    ? `${option.name} (${option.hostname || '?'})`
                                    : option.hostname || `Device #${option.id}`
                            }
                            getOptionDisabled={(option) => assignedDeviceIds.has(option.id)}
                            isOptionEqualToValue={(option, val) => option.id === val.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="RPi zařízení"
                                    fullWidth
                                    helperText="Vyberte Raspberry Pi zařízení pro tento objekt (nepovinné)"
                                />
                            )}
                            renderOption={(props, option) => {
                                const isAssigned = assignedDeviceIds.has(option.id);
                                const { key, ...rest } = props;
                                return (
                                    <li key={option.id} {...rest}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <Box>
                                                <Typography>
                                                    {option.name || option.hostname || `Device #${option.id}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.hostname && option.name ? option.hostname + ' | ' : ''}
                                                    {option.ipAddress || ''}
                                                </Typography>
                                            </Box>
                                            {isAssigned && (
                                                <Chip label="Přiřazeno" size="small" color="warning" variant="outlined" />
                                            )}
                                        </Box>
                                    </li>
                                );
                            }}
                            clearOnEscape
                        />

                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                            Adresní údaje pro vytvoření poplachu
                        </Typography>

                        <Autocomplete
                            freeSolo
                            options={lokaceOptions}
                            loading={isLoadingLokace}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.nazev}
                            filterOptions={(x) => x}
                            inputValue={adresaInput}
                            onInputChange={(_, value) => handleAdresaInputChange(value)}
                            onChange={(_, value) => handleLokaceSelect(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Vyhledat adresu"
                                    fullWidth
                                    placeholder="Zadejte obec, ulici nebo adresu..."
                                    helperText="Vyberte z nabídky pro automatické vyplnění polí"
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                    <li key={option.id} {...rest}>
                                        <Box>
                                            <Typography>{option.nazev}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.displayName}
                                            </Typography>
                                        </Box>
                                    </li>
                                );
                            }}
                        />

                        <TextField
                            label="Obec"
                            value={formData.obec || ''}
                            onChange={(e) => setFormData({ ...formData, obec: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                            helperText="Povinné - název obce"
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Část obce"
                                value={formData.castObce || ''}
                                onChange={(e) => setFormData({ ...formData, castObce: e.target.value })}
                                fullWidth
                                inputProps={{ maxLength: 200 }}
                            />
                            <TextField
                                label="Ulice"
                                value={formData.ulice || ''}
                                onChange={(e) => setFormData({ ...formData, ulice: e.target.value })}
                                fullWidth
                                inputProps={{ maxLength: 200 }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <TextField
                                label="Číslo popisné"
                                value={formData.cisloPopisne || ''}
                                onChange={(e) => setFormData({ ...formData, cisloPopisne: e.target.value })}
                                sx={{ width: '30%' }}
                                inputProps={{ maxLength: 50 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                                <TextField
                                    label="GPS souřadnice"
                                    value={formData.gps || ''}
                                    onChange={(e) => setFormData({ ...formData, gps: e.target.value })}
                                    fullWidth
                                    inputProps={{ maxLength: 100 }}
                                    placeholder="49.1234, 16.5678"
                                    required
                                    error={!!formData.gps && !isValidGps(formData.gps)}
                                    helperText={
                                        formData.gps && !isValidGps(formData.gps)
                                            ? 'Neplatný formát/rozsah (lat,lng)'
                                            : 'Povinné. Formát: lat, lng'
                                    }
                                />
                                <Tooltip title="Vybrat na mapě">
                                    <IconButton
                                        onClick={() => setMapPickerOpen(true)}
                                        color="primary"
                                        sx={{ mt: 1 }}
                                    >
                                        <MapIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <TextField
                            label="Poznámka k adrese"
                            value={formData.adresa || ''}
                            onChange={(e) => setFormData({ ...formData, adresa: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            inputProps={{ maxLength: 500 }}
                            helperText="Doplňující informace k adrese (nepovinné)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            !formData.nazev.trim() ||
                            !formData.obec?.trim() ||
                            !isValidGps(formData.gps) ||
                            createMutation.isPending ||
                            updateMutation.isPending
                        }
                    >
                        {(createMutation.isPending || updateMutation.isPending) && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {selectedObjekt ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat objekt?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat objekt <strong>{selectedObjekt?.nazev}</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Budou smazány i všechny senzory a historie událostí tohoto objektu!
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Map Picker Dialog */}
            <MapPickerDialog
                open={mapPickerOpen}
                onClose={() => setMapPickerOpen(false)}
                onSelect={handleGpsSelect}
                initialGps={formData.gps || undefined}
                geocodedObecGps={geocodedGps}
            />

            {/* Onboarding Wizard (bez konkrétního objektu — instruktážní režim) */}
            <OnboardingWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
            />
        </Box>
    );
};

export default IoTObjektyPage;
